<template>
  <div class="checkout-page">
    <!-- 头部 -->
    <div class="page-header">
      <h1>{{ $t('customer.Checkout.line5_text') }}</h1>
    </div>

    <div class="checkout-content">
      <!-- 收货地址 -->
      <div class="section">
        <div class="section-title">
          {{ $t('customer.Checkout.deliveryAddress') }}
          <button class="btn-manage" @click="goToAddressManage">
            {{ $t('customer.Checkout.manageAddress') }}
          </button>
        </div>
        
        <div v-if="loadingAddresses" class="loading">
          <div class="loading-icon">⏳</div>
          <p>{{ $t('customer.Checkout.line20_text') }}</p>
        </div>
        
        <div v-else-if="addresses.length === 0" class="empty-address">
          <div class="empty-icon">📍</div>
          <p>{{ $t('customer.Checkout.line25_text') }}</p>
          <button class="btn-add-address" @click="goToAddressManage">
            {{ $t('customer.Checkout.addAddress') }}
          </button>
        </div>
        
        <div v-else class="address-selector">
          <div 
            v-for="addr in addresses" 
            :key="addr.id" 
            class="address-option" 
            :class="{ selected: selectedAddressId === addr.id }"
            @click="selectAddress(addr.id)"
          >
            <div class="address-radio">
              <input 
                type="radio" 
                :checked="selectedAddressId === addr.id" 
                @change="selectAddress(addr.id)"
              />
              <span v-if="addr.isDefault" class="badge-default">{{ $t('customer.Checkout.line45_text') }}</span>
            </div>
            <div class="address-detail">
              <div class="address-contact">{{ addr.contactName }} · {{ addr.contactPhone }}</div>
              <div class="address-full">{{ addr.deliveryAddress }}</div>
            </div>
          </div>
        </div>
        
        <!-- 隐藏的表单字段，用于提交 -->
        <input type="hidden" v-model="form.addressId" />
      </div>

      <!-- 商品清单 -->
      <div class="section">
        <div class="section-title">{{ $t('customer.Checkout.line60_text') }}</div>
        <div class="items-list">
          <div v-for="item in cartItems" :key="item.id" class="item-row">
            <div class="item-info">
              <div class="item-name">{{ item.name }}</div>
              <div class="item-price">S$ {{ (item.price || 0).toFixed(2) }} × {{ item.quantity }}</div>
              <div class="item-tax" v-if="item.taxRate && item.taxRate > 0">
                {{ $t('customer.Checkout.taxInfo', { rate: (item.taxRate * 100).toFixed(1), tax: ((item.price || 0) * item.quantity * item.taxRate).toFixed(2) }) }}
              </div>
              <div class="item-tax item-tax-free" v-else-if="item.taxRate === 0">
                {{ $t('customer.Checkout.taxFree') }}
              </div>
            </div>
            <div class="item-subtotal">S$ {{ ((item.price || 0) * item.quantity).toFixed(2) }}</div>
          </div>
        </div>
      </div>

      <!-- 支付方式 -->
      <div class="section">
        <div class="section-title">{{ $t('customer.Checkout.line80_text') }}</div>
        <div class="radio-group">
          <label class="radio-label">
            <input type="radio" v-model="form.paymentMethod" value="COD" />
            <span>{{ $t('customer.Checkout.line84_text') }}</span>
          </label>
          <label class="radio-label">
            <input type="radio" v-model="form.paymentMethod" value="PAYNOW" />
            <span>{{ $t('customer.Checkout.line88_text') }}</span>
          </label>
          <label class="radio-label">
            <input type="radio" v-model="form.paymentMethod" value="BANK_TRANSFER" />
            <span>{{ $t('customer.Checkout.line92_text') }}</span>
          </label>
        </div>

        <!-- ⭐ PayNow 收款码展示 -->
        <div v-if="form.paymentMethod === 'PAYNOW'" class="paynow-qr-section">
          <div v-if="loadingPayNowQr" class="paynow-qr-loading">
            <div class="loading-icon">⏳</div>
            <p>{{ $t('customer.Checkout.line100_text') }}</p>
          </div>
          <div v-else-if="paynowQrUrl" class="paynow-qr-display">
            <div class="paynow-qr-title">{{ $t('customer.Checkout.line103_text') }}</div>
            <div class="paynow-qr-image-container">
              <img :src="getImageUrl(paynowQrUrl)" alt="PayNow QR Code" class="paynow-qr-image" />
            </div>
          </div>
          <div v-else class="paynow-qr-empty">
            <p>{{ $t('customer.Checkout.line109_text') }}</p>
            <p>{{ $t('customer.Checkout.line110_text') }}</p>
          </div>
        </div>

        <!-- ⭐ 银行账户信息 -->
        <div v-if="form.paymentMethod === 'BANK_TRANSFER'" class="bank-info-section">
          <div v-if="loadingBankInfo" class="paynow-qr-loading">
            <p>{{ $t('customer.Checkout.line117_text') }}</p>
          </div>
          <div v-else-if="bankInfo.bankName" class="bank-info-display">
            <div class="bank-info-title">{{ $t('customer.Checkout.line120_text') }}</div>
            <div class="bank-info-row">
              <span class="bank-label">{{ $t('customer.Checkout.line122_text') }}</span>
              <span class="bank-value">{{ bankInfo.bankName }}</span>
            </div>
            <div class="bank-info-row">
              <span class="bank-label">{{ $t('customer.Checkout.line126_text') }}</span>
              <span class="bank-value">{{ bankInfo.bankAccountName }}</span>
            </div>
            <div class="bank-info-row">
              <span class="bank-label">{{ $t('customer.Checkout.line130_text') }}</span>
              <span class="bank-value highlight">{{ bankInfo.bankAccountNumber }}</span>
            </div>
          </div>
          <div v-else class="paynow-qr-empty">
            <p>{{ $t('customer.Checkout.line135_text') }}</p>
            <p>{{ $t('customer.Checkout.line136_text') }}</p>
          </div>
        </div>

        <!-- ⭐ 支付凭证上传 (PayNow / Bank Transfer) -->
        <div v-if="(form.paymentMethod === 'PAYNOW' || form.paymentMethod === 'BANK_TRANSFER') && (paynowQrUrl || bankInfo.bankName)" class="payment-proof-section">
          <div class="proof-title">{{ $t('customer.Checkout.line142_text') }}</div>
          <div class="proof-upload-area" @click="triggerProofUpload">
            <img v-if="paymentProofUrl" :src="getImageUrl(paymentProofUrl)" alt="Payment Proof" class="proof-image" />
            <div v-else class="proof-placeholder">
              <span class="proof-icon">📤</span>
              <p>{{ $t('customer.Checkout.line147_text') }}</p>
              <p class="proof-hint">{{ $t('customer.Checkout.line148_text') }}</p>
            </div>
          </div>
          <input ref="proofInput" type="file" accept="image/*" style="display:none" @change="handleProofUpload" />
          <div v-if="paymentProofUrl" class="proof-tip">{{ $t('customer.Checkout.line152_text') }}</div>
        </div>
      </div>

      <!-- 配送方式 -->
      <div class="section">
        <div class="section-title">{{ $t('customer.Checkout.line158_text') }}</div>
        <div class="radio-group">
          <label class="radio-label">
            <input type="radio" v-model="form.deliveryMethod" value="DELIVERY" />
            <span>{{ $t('customer.Checkout.line162_text') }}</span>
          </label>
          <!-- 自提选项暂时隐藏 -->
          <!--
          <label class="radio-label">
            <input type="radio" v-model="form.deliveryMethod" value="SELF_PICKUP" />
            <span>{{ $t('customer.Checkout.line168_text') }}</span>
          </label>
          -->
        </div>
      </div>

      <!-- 备注 -->
      <div class="section">
        <div class="section-title">{{ $t('customer.Checkout.line176_text') }}</div>
        <textarea
          v-model="form.remark"
          class="form-textarea"
          rows="3"
          :placeholder="$t('customer.Checkout.line181_placeholder')"
        ></textarea>
      </div>

      <!-- 金额汇总 -->
      <div class="section amount-summary">
        <div class="summary-row">
          <span>{{ $t('customer.Checkout.line188_text') }}</span>
          <span>S$ {{ subtotal.toFixed(2) }}</span>
        </div>
        <div class="summary-row">
          <span>{{ $t('customer.Checkout.line192_text') }}</span>
          <span>S$ {{ gst.toFixed(2) }}</span>
        </div>
        <div class="summary-row total-row">
          <span>{{ $t('customer.Checkout.line196_text') }}</span>
          <span>S$ {{ totalWithGst.toFixed(2) }}</span>
        </div>
      </div>

      <!-- 提交按钮 -->
      <div class="submit-btn">
        <button class="btn btn-primary" @click="submitOrder" :disabled="!canSubmit">
          {{ $t('customer.Checkout.submitOrder') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import addressApi from '@/services/address';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'Checkout',
  data() {
    return {
      lang: 'zh',  // 语言切换
      cartItems: [],
      addresses: [],
      selectedAddressId: null,
      loadingAddresses: false,
      // ⭐ PayNow 收款码
      paynowQrUrl: '',
      loadingPayNowQr: false,
      // ⭐ 银行收款信息
      bankInfo: { bankName: '', bankAccountName: '', bankAccountNumber: '' },
      loadingBankInfo: false,
      // ⭐ 支付凭证
      paymentProofUrl: '',
      // ⭐ 批发价包
      packageToken: null,
      form: {
        addressId: null,
        contactName: '',
        contactPhone: '',
        deliveryAddress: '',
        paymentMethod: 'COD',
        deliveryMethod: 'DELIVERY',
        remark: ''
      }
    };
  },
  computed: {
    selectedAddress() {
      return this.addresses.find(a => a.id === this.selectedAddressId) || null;
    },
    subtotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    },
    gst() {
      return this.cartItems.reduce((sum, item) => {
        const taxRate = item.taxRate || 0
        return sum + (item.price * item.quantity * taxRate)
      }, 0)
    },
    totalWithGst() {
      return this.subtotal + this.gst;
    },
    canSubmit() {
      // 只检查配送地址和商品，联系人信息可以为空（从地址获取）
      return this.form.deliveryAddress && 
             this.cartItems.length > 0;
    }
  },
  async created() {
    // ⭐ 批发价包 token
    this.packageToken = this.$route.query.packageToken || null;
    
    // 优先从路由参数获取选中的商品（支持部分结算）
    if (this.$route.query.items) {
      try {
        this.cartItems = JSON.parse(this.$route.query.items);
      } catch (e) {
        console.error('解析商品数据失败:', e);
        // 解析失败则从 localStorage 加载全部购物车
        this.loadCartFromLocalStorage();
      }
    } else {
      // 从 localStorage 加载全部购物车
      this.loadCartFromLocalStorage();
    }
    
    if (this.cartItems.length === 0) {
      this.$message.error(this.$t('customer.Checkout.cartEmpty'));
      this.$router.push('/customer/stall-products');
      return;
    }
    
    // 加载收货地址（同步等待完成）
    await this.loadAddresses();
    
    // ⭐ 加载 PayNow 收款码
    this.loadPayNowQr();
    // ⭐ 加载银行信息
    this.loadBankInfo();
  },
  methods: {
    getImageUrl,
    async loadAddresses() {
      this.loadingAddresses = true;
      try {
        console.log('开始加载地址列表...');
        const response = await addressApi.getList();
        console.log('地址列表响应:', response);
        
        // 响应可能是直接的数据数组，也可能是包含 data 字段的对象
        this.addresses = Array.isArray(response) ? response : (response.data || []);
        console.log('地址列表:', this.addresses);
        
        // 自动选择默认地址
        const defaultAddr = this.addresses.find(addr => addr.isDefault);
        if (defaultAddr) {
          this.selectedAddressId = defaultAddr.id;
          this.form.addressId = defaultAddr.id;
          this.form.contactName = defaultAddr.contactName;
          this.form.contactPhone = defaultAddr.contactPhone;
          this.form.deliveryAddress = defaultAddr.deliveryAddress;
          console.log('自动选择默认地址:', defaultAddr);
        }
      } catch (error) {
        console.error('加载地址失败:', error);
        console.error('错误详情:', error.response?.data || error.message);
      } finally {
        this.loadingAddresses = false;
      }
    },
    selectAddress(id) {
      const addr = this.addresses.find(a => a.id === id);
      if (addr) {
        this.selectedAddressId = id;
        this.form.addressId = id;
        this.form.contactName = addr.contactName;
        this.form.contactPhone = addr.contactPhone;
        this.form.deliveryAddress = addr.deliveryAddress;
      }
    },
    goToAddressManage() {
      this.$router.push('/stall/addresses');
    },
    
    // ⭐ 加载 PayNow 收款码
    async loadPayNowQr() {
      try {
        this.loadingPayNowQr = true;
        const wholesalerId = this.cartItems[0]?.wholesalerId
        console.log('🔍 加载 PayNow 收款码, wholesalerId:', wholesalerId)
        if (!wholesalerId) {
          console.warn('⚠️ 未获取到批发商ID');
          return;
        }
        const response = await api.get('/shop/info', { params: { wholesalerId } });
        console.log('📦 /shop/info 响应:', JSON.stringify(response, null, 2))
        if (response && response.paynowQrUrl) {
          this.paynowQrUrl = response.paynowQrUrl;
          console.log('✅ PayNow 收款码加载成功:', this.paynowQrUrl);
        } else {
          this.paynowQrUrl = '';
          console.log('⚠️ 未找到 PayNow 收款码, response keys:', response ? Object.keys(response) : 'null');
        }
      } catch (error) {
        console.error('❌ 加载 PayNow 收款码失败:', error);
        this.paynowQrUrl = '';
      } finally {
        this.loadingPayNowQr = false;
      }
    },
    // ⭐ 加载银行收款信息
    async loadBankInfo() {
      try {
        this.loadingBankInfo = true;
        const wholesalerId = this.cartItems[0]?.wholesalerId
        if (!wholesalerId) return
        const response = await api.get('/shop/info', { params: { wholesalerId } })
        if (response) {
          this.bankInfo = {
            bankName: response.bankName || '',
            bankAccountName: response.bankAccountName || '',
            bankAccountNumber: response.bankAccountNumber || ''
          }
          console.log('✅ 银行信息加载成功:', this.bankInfo)
        }
      } catch (error) {
        console.error('❌ 加载银行信息失败:', error)
      } finally {
        this.loadingBankInfo = false
      }
    },
    // ⭐ 上传支付凭证
    triggerProofUpload() {
      this.$refs.proofInput.click()
    },
    async handleProofUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/files/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res && res.url) {
          this.paymentProofUrl = res.url
          console.log('✅ 支付凭证上传成功:', res.url)
        }
      } catch (error) {
        console.error('❌ 上传失败:', error)
        this.$message.error(this.$t('customer.Checkout.uploadFailed'))
      } finally {
        event.target.value = ''
      }
    },
    loadCartFromLocalStorage() {
      try {
        const cartStr = localStorage.getItem('stall_cart');
        this.cartItems = cartStr ? JSON.parse(cartStr) : [];
      } catch (e) {
        console.error('加载购物车失败:', e);
        this.cartItems = [];
      }
    },
    async submitOrder() {
      if (!this.canSubmit) {
        this.$message.error(this.$t('customer.Checkout.pleaseFillAll'));
        return;
      }

      // ✅ 验证：检查零库存和 0 元订单
      const invalidItems = this.cartItems.filter(item => {
        const hasZeroStock = item.availableStock !== undefined && item.availableStock <= 0;
        const hasZeroPrice = item.price !== undefined && item.price <= 0;
        return hasZeroStock || hasZeroPrice;
      });

      if (invalidItems.length > 0) {
        const errorMsg = this.lang === 'zh'
          ? this.$t('customer.Checkout.invalidItemsZh') + '\n' +
            invalidItems.map(item => {
              const reasons = [];
              if (item.availableStock !== undefined && item.availableStock <= 0) {
                reasons.push(this.$t('customer.Checkout.outOfStock'));
              }
              if (item.price !== undefined && item.price <= 0) {
                reasons.push(this.$t('customer.Checkout.zeroPrice'));
              }
              return `- ${item.name}（${reasons.join(', ')}）`;
            }).join('\n') + '\n\n' + this.$t('customer.Checkout.adjustInCart')
          : this.$t('customer.Checkout.invalidItemsEn') + '\n' +
            invalidItems.map(item => {
              const reasons = [];
              if (item.availableStock !== undefined && item.availableStock <= 0) {
                reasons.push(this.$t('customer.Checkout.outOfStock'));
              }
              if (item.price !== undefined && item.price <= 0) {
                reasons.push(this.$t('customer.Checkout.zeroPrice'));
              }
              return `- ${item.name} (${reasons.join(', ')})`;
            }).join('\n') + '\n\n' + this.$t('customer.Checkout.adjustInCart');
        
        this.$message.error(errorMsg);
        return;
      }

      try {
        // 根据当前访问路径确定 sourceChannel
        const currentPath = window.location.pathname;
        let sourceChannel = 'APP';
        if (currentPath.includes('h5') || currentPath.includes('package')) {
          sourceChannel = 'H5';
        } else if (currentPath.includes('wholesaler')) {
          sourceChannel = 'WHOLESALER';
        }
        
        const orderData = {
          orderType: 'SELF',
          sourceChannel: sourceChannel,
          customerId: parseInt(localStorage.getItem('stored_customer_id')) || null,
          customerName: this.form.contactName,
          customerPhone: this.form.contactPhone,
          packageId: this.$route.query.packageId || null,
          packageToken: this.$route.query.packageToken || null,
          wholesalerId: this.cartItems[0]?.wholesalerId || null,
          items: this.cartItems.map(item => {
            console.log('商品:', item.name, '提交价格:', item.price);
            return {
              productId: item.id,
              quantity: item.quantity,
              unitPrice: item.price  // 传递单价（批发价或市场价）
            };
          }),
          paymentMethod: this.form.paymentMethod,
          paymentProofUrl: this.paymentProofUrl || null,
          // 上传了凭证 = 已支付（待确认）
          paymentStatus: this.paymentProofUrl ? 'paid' : 'unpaid',
          deliveryMethod: this.form.deliveryMethod,
          deliveryAddress: this.form.deliveryAddress,
          deliveryRegionCode: null,
          deliveryAddressLat: this.selectedAddress?.latitude || null,
          deliveryAddressLng: this.selectedAddress?.longitude || null,
          remark: this.form.remark,
          subtotal: this.subtotal,
          gstRate: null,
          gstAmount: this.gst,
          totalAmount: this.totalWithGst
        };

        console.log('📦 提交订单:', orderData);
        console.log('📍 selectedAddress:', this.selectedAddress);
        console.log('📍 deliveryAddressLat:', orderData.deliveryAddressLat, 'deliveryAddressLng:', orderData.deliveryAddressLng);

        console.log('提交订单 / Submit order:', orderData);
        console.log('购物车商品详情:', this.cartItems);
        
        // 调用后端 API
        const response = await api.post('/orders', orderData);
        console.log('订单提交响应:', response);
        
        this.$message.success(this.$t('customer.Checkout.orderSubmitted'));
        
        // 如果是从路由参数来的（部分结算），只删除选中的商品
        if (this.$route.query.items) {
          const allCart = JSON.parse(localStorage.getItem('stall_cart') || '[]');
          const remainingCart = allCart.filter(item => 
            !this.cartItems.find(selected => selected.id === item.id)
          );
          localStorage.setItem('stall_cart', JSON.stringify(remainingCart));
        } else {
          // 否则清空购物车
          localStorage.removeItem('stall_cart');
        }
        
        // 跳转到订单列表
        this.$router.push('/stall/orders');
      } catch (error) {
        console.error('提交订单失败 / Submit order failed:', error);
        console.log('Error object:', error);
        console.log('Error response:', error.response);
        console.log('Error message:', error.message);
        
        // ✅ 解析后端返回的错误信息
        // 注意：axios 拦截器已经处理过，error 是一个 Error 对象
        // error.message 包含后端返回的 message 字段
        let errorMsg = error.message || this.$t('customer.Checkout.submitFailed');
        let errorDetails = null;
        
        // 尝试解析错误详情
        errorDetails = this.parseErrorDetails(errorMsg);
        
        // ✅ 根据错误类型显示不同的提示
        if (errorDetails) {
          if (errorDetails.type === 'PRICE_ERROR') {
            // 价格验证失败
            this.showPriceUpdateConfirm(errorDetails);
            return;
          } else if (errorDetails.type === 'STOCK_ERROR') {
            // 库存验证失败
            this.showStockUpdateConfirm(errorDetails);
            return;
          }
        }
        
        // 其他错误，显示普通提示
        this.$message.error(errorMsg);
      }
    },
    
    /**
     * 解析错误详情
     * 格式：TYPE:productName:submittedPrice:currentPrice 或 TYPE:productId:availableStock:orderQty
     * 多个错误用分号分隔：PRICE_ERROR:...; STOCK_ERROR:...
     */
    parseErrorDetails(errorMsg) {
      if (!errorMsg) return null;
      
      // 分割多个错误（用分号分隔）
      const errors = errorMsg.split('; ').map(s => s.trim());
      
      // 解析每个错误
      for (const error of errors) {
        // 价格错误：PRICE_ERROR:商品名：提交价格：当前价格
        const priceMatch = error.match(/^PRICE_ERROR:([^:]+):(\d+\.?\d*):(\d+\.?\d*)$/);
        if (priceMatch) {
          return {
            type: 'PRICE_ERROR',
            productName: priceMatch[1],
            submittedPrice: parseFloat(priceMatch[2]),
            currentPrice: parseFloat(priceMatch[3])
          };
        }
        
        // 库存错误：STOCK_ERROR:商品 ID:可用库存：订单数量
        const stockMatch = error.match(/^STOCK_ERROR:(\d+):(\d+\.?\d*):(\d+\.?\d*)$/);
        if (stockMatch) {
          return {
            type: 'STOCK_ERROR',
            productId: parseInt(stockMatch[1]),
            availableStock: parseFloat(stockMatch[2]),
            orderQty: parseFloat(stockMatch[3])
          };
        }
      }
      
      return null;
    },
    
    /**
     * 显示价格更新确认框
     */
    async showPriceUpdateConfirm(details) {
      const msg = this.lang === 'zh' 
        ? this.$t('customer.Checkout.priceChangedZh', { name: details.productName, submitted: details.submittedPrice.toFixed(2), current: details.currentPrice.toFixed(2) })
        : this.$t('customer.Checkout.priceChangedEn', { name: details.productName, submitted: details.submittedPrice.toFixed(2), current: details.currentPrice.toFixed(2) });
      
      try {
        await this.$confirm(msg, this.$t('customer.Checkout.hint'), {
          confirmButtonText: this.$t('customer.Checkout.confirm'),
          cancelButtonText: this.$t('customer.Checkout.cancel'),
          type: 'warning',
          closeOnClickModal: false
        });
        // 用户确认更新价格
        this.updateCartPrice(details);
      } catch {
        // 用户取消
      }
    },
    
    /**
     * 更新购物车价格
     */
    updateCartPrice(details) {
      // 注意：details 里没有 productId，需要从 cartItems 中查找
      const productName = details.productName;
      
      // 更新本地购物车
      const updatedCart = this.cartItems.map(item => {
        if (item.name === productName) {
          console.log('更新前价格:', item.price, '更新后价格:', details.currentPrice);
          return { ...item, price: details.currentPrice };
        }
        return item;
      });
      
      this.cartItems = updatedCart;
      
      // 保存到 localStorage
      localStorage.setItem('stall_cart', JSON.stringify(updatedCart));
      
      this.$message.success(this.lang === 'zh' ? this.$t('customer.Checkout.priceUpdatedZh') : this.$t('customer.Checkout.priceUpdatedEn'));
    },
    
    /**
     * 显示库存更新确认框
     */
    async showStockUpdateConfirm(details) {
      const msg = this.lang === 'zh'
        ? this.$t('customer.Checkout.stockInsufficientZh', { available: details.availableStock.toFixed(2), ordered: details.orderQty.toFixed(2) })
        : this.$t('customer.Checkout.stockInsufficientEn', { available: details.availableStock.toFixed(2), ordered: details.orderQty.toFixed(2) });
      
      try {
        await this.$confirm(msg, this.$t('customer.Checkout.hint'), {
          confirmButtonText: this.$t('customer.Checkout.confirm'),
          cancelButtonText: this.$t('customer.Checkout.cancel'),
          type: 'warning',
          closeOnClickModal: false
        });
        // 用户确认更新库存
        this.updateCartStock(details);
      } catch {
        // 用户取消
      }
    },
    
    /**
     * 更新购物车库存数量
     */
    updateCartStock(details) {
      const updatedCart = this.cartItems.map(item => {
        if (item.id === details.productId) {
          console.log('商品 ID:', item.id, '更新前数量:', item.quantity, '更新后数量:', details.availableStock);
          return { ...item, quantity: details.availableStock };
        }
        return item;
      });
      
      this.cartItems = updatedCart;
      
      // 保存到 localStorage
      localStorage.setItem('stall_cart', JSON.stringify(updatedCart));
      
      this.$message.success(this.lang === 'zh' ? this.$t('customer.Checkout.stockUpdatedZh') : this.$t('customer.Checkout.stockUpdatedEn'));
    }
  }
};
</script>

<style lang="scss" scoped>
.checkout-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.page-header {
  background: white;
  padding: calc(16px + env(safe-area-inset-top, 0px)) 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  
  h1 {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.checkout-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .btn-manage {
      background: #667eea;
      color: white;
      border: none;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        background: #5568d3;
      }
    }
  }
}

/* 地址选择器 */
.address-selector {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-option {
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.address-option:hover {
  background: #f9f9f9;
}

.address-option.selected {
  border-color: #667eea;
  background: #f0f4ff;
}

.address-radio {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.address-radio input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.address-detail {
  flex: 1;
}

.address-contact {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.address-full {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.badge-default {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 8px;
  margin-left: 4px;
}

.badge-label {
  background: #f0f0f0;
  color: #666;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 8px;
  margin-left: 4px;
}

.loading,
.empty-address {
  text-align: center;
  padding: 40px 20px;
  background: #f9f9f9;
  border-radius: 8px;
}

.loading-icon,
.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.loading p,
.empty-address p {
  color: #999;
  margin-bottom: 16px;
}

.btn-add-address {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-address:hover {
  opacity: 0.9;
}

/* 表单样式 */
.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  
  .required {
    color: #f5576c;
  }
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: #667eea;
  }
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

/* 单选按钮组 */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  span {
    font-size: 14px;
    color: #333;
  }
  
  &:has(input:checked) {
    border-color: #667eea;
    background: #f0f4ff;
  }
}

/* ⭐ PayNow 收款码样式 */
.paynow-qr-section {
  margin-top: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  border: 2px solid #38bdf8;
}

.paynow-qr-loading {
  text-align: center;
  padding: 20px;
  
  .loading-icon {
    font-size: 36px;
    margin-bottom: 8px;
    animation: spin 1s linear infinite;
  }
  
  p {
    color: #64748b;
    font-size: 14px;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.paynow-qr-display {
  text-align: center;
  
  .paynow-qr-title {
    font-size: 16px;
    font-weight: 600;
    color: #1e40af;
    margin-bottom: 16px;
  }
  
  .paynow-qr-image-container {
    background: white;
    border-radius: 12px;
    padding: 20px;
    display: inline-block;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    
    .paynow-qr-image {
      max-width: 280px;
      width: 100%;
      height: auto;
      border-radius: 8px;
    }
  }
  
  .paynow-qr-tip {
    margin-top: 16px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 8px;
    font-size: 13px;
    color: #475569;
  }
}

.paynow-qr-empty {
  text-align: center;
  padding: 20px;
  
  p {
    margin: 8px 0;
    font-size: 14px;
    color: #94a3b8;
  }
}

/* ⭐ 银行收款信息样式 */
.bank-info-section {
  margin-top: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border-radius: 12px;
  border: 2px solid #eab308;
}

.bank-info-display {
  .bank-info-title {
    font-size: 16px;
    font-weight: 600;
    color: #854d0e;
    margin-bottom: 16px;
  }
  
  .bank-info-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid rgba(234, 179, 8, 0.2);
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .bank-label {
    font-size: 13px;
    color: #713f12;
    font-weight: 500;
  }
  
  .bank-value {
    font-size: 14px;
    color: #1e293b;
    font-weight: 600;
    
    &.highlight {
      color: #0369a1;
      font-size: 16px;
    }
  }
}

/* ⭐ 支付凭证上传样式 */
.payment-proof-section {
  margin-top: 16px;
  padding: 20px;
  background: #f0fdf4;
  border-radius: 12px;
  border: 2px solid #22c55e;
  
  .proof-title {
    font-size: 14px;
    font-weight: 600;
    color: #166534;
    margin-bottom: 12px;
  }
  
  .proof-upload-area {
    background: white;
    border: 2px dashed #86efac;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      border-color: #22c55e;
      background: #f0fdf4;
    }
    
    .proof-image {
      max-width: 280px;
      width: 100%;
      height: auto;
      border-radius: 8px;
    }
    
    .proof-placeholder {
      .proof-icon {
        font-size: 48px;
      }
      
      p {
        margin: 8px 0 4px;
        font-size: 14px;
        color: #166534;
        font-weight: 500;
      }
      
      .proof-hint {
        font-size: 12px;
        color: #86efac;
      }
    }
  }
  
  .proof-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #16a34a;
    text-align: center;
  }
}

.items-list {
  .item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 8px;
    
    .item-info {
      flex: 1;
      
      .item-name {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
      }
      
      .item-price {
        font-size: 13px;
        color: #666;
      }
      
      .item-tax {
        font-size: 11px;
        color: #16a34a;
        margin-top: 2px;
      }
      
      .item-tax-free {
        color: #f59e0b;
      }
    }
    
    .item-subtotal {
      font-size: 15px;
      font-weight: 600;
      color: #f5576c;
    }
  }
}

.amount-summary {
  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 14px;
    color: #666;
    
    &.total-row {
      font-size: 18px;
      font-weight: 700;
      color: #333;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 2px solid #f0f0f0;
    }
  }
}

.submit-btn {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 16px 20px;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.05);
  
  .btn {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    
    &.btn-primary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
